import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const Hero = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slidesData = [
    {
      image: "/images/colmena_portada.webp",
      title: "Productos Apícolas Artesanales",
      text: "Descubre la pureza y el sabor de los productos derivados de las abejas. Miel, cera, propóleo y más, directamente de la naturaleza a tu mesa.",
      type: "home"
    },
    {
      image: "/images/triptico_2.webp", 
      title: "Conoce Todo Sobre Nuestro Proceso",
      text: "Visualiza o descarga nuestro tríptico informativo sobre nuestros productos.",
      type: "triptico"
    }
  ];

  // 1. Efecto del carrusel que se detiene cuando cambias de pestaña
  useEffect(() => {
    let slideInterval;

    const startTimer = () => {
      slideInterval = setInterval(() => {
        setCurrentSlide((prev) => (prev === slidesData.length - 1 ? 0 : prev + 1));
      }, 8000);
    };

    startTimer();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(slideInterval);
      } else {
        startTimer();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(slideInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [slidesData.length]);

  // 2. Efecto para mostrar/ocultar el botón "Ir Arriba"
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderSlideButtons = (slideType) => {
    if (slideType === "triptico") {
      return (
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full">
          <a 
            href="/docs/triptico_paa.pdf" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="w-full sm:w-auto text-center bg-orange-500 dark:bg-[#FFD54F] hover:bg-orange-600 dark:hover:bg-[#FFE082] text-white dark:text-[#1C1B17] font-extrabold py-4 px-10 rounded-full transition duration-300 transform hover:scale-105 shadow-lg cursor-pointer uppercase tracking-wider"
          >
            Ver Tríptico
          </a>
          <a 
            href="/docs/triptico_paa.pdf" 
            download="Triptico-Apicultura-Artesanal.pdf" 
            className="w-full sm:w-auto text-center bg-white/20 dark:bg-white/10 hover:bg-white/30 text-white font-extrabold py-4 px-10 rounded-full transition duration-300 transform hover:scale-105 border-2 border-white/50 cursor-pointer uppercase tracking-wider"
          >
            Descargar Tríptico
          </a>
        </div>
      );
    } else {
      return (
        <div className="flex justify-center items-center w-full">
          <a 
            href="/#about" 
            className="bg-orange-500 dark:bg-[#FFD54F] hover:bg-orange-600 dark:hover:bg-[#FFE082] text-white dark:text-[#1C1B17] font-extrabold py-4 px-12 rounded-full transition duration-300 transform hover:scale-105 shadow-lg hover:shadow-orange-500/50 dark:hover:shadow-yellow-500/50 cursor-pointer uppercase tracking-wider"
          >
            Conoce Más 
          </a>
        </div>
      );
    }
  };

  return (
    <>
      {/* 👇 AQUÍ ESTÁ EL BLOQUEO: h-[calc(100vh-80px)] min-h-[600px] w-full SIN paddings verticales 👇 */}
      <section className="relative h-[calc(100vh-80px)] min-h-[600px] w-full flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden">
        
        {/* IMÁGENES DE FONDO */}
        {slidesData.map((slide, index) => (
          <div 
            key={index}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center" 
              style={{backgroundImage: `url(${slide.image})`}}
            ></div>
            <div className="absolute inset-0 bg-black/70 dark:bg-[#1C1B17]/85 transition-colors duration-500"></div>
          </div>
        ))}
        
        {/* CONTENIDO TEXTUAL */}
        <div className="relative z-20 w-full max-w-7xl mx-auto transform -translate-y-10 md:-translate-y-16 min-h-[350px] flex flex-col justify-center items-center text-center">
          
          {/* OPTIMIZACIÓN SEO: H1 estático oculto visualmente */}
          <h1 className="sr-only">Productos Apícolas Artesanales - Miel, Propóleo y Derivados en Zacoalco de Torres</h1>
          
          {/* OPTIMIZACIÓN SEO: El título animado ahora es un H2, conservando EXACTAMENTE tus clases originales */}
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white dark:text-transparent dark:bg-gradient-to-r dark:from-[#FFD54F] dark:via-[#FFE082] dark:to-white dark:bg-clip-text mb-8 leading-tight drop-shadow-2xl">
            {slidesData[currentSlide].title}
          </h2>
          
          <p className="text-lg sm:text-xl md:text-2xl text-amber-50 dark:text-[#FFE082]/90 mb-12 max-w-3xl mx-auto leading-relaxed font-semibold drop-shadow-md">
            {slidesData[currentSlide].text}
          </p>
          
          {renderSlideButtons(slidesData[currentSlide].type)}
          
        </div>

        {/* INDICADORES (Dots) */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-30 flex gap-4">
          {slidesData.map((_, index) => (
            <button 
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-500 ${index === currentSlide ? 'bg-orange-500 w-8' : 'bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      </section>

      {/* BOTONES FLOTANTES */}
      <div className="fixed bottom-8 right-8 z-50 flex flex-col gap-4 items-end">
        <button 
          onClick={scrollToTop}
          className={`p-3 md:p-4 rounded-full bg-gray-800/90 text-white shadow-2xl backdrop-blur-sm transition-all duration-300 hover:scale-110 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>

        <button 
          onClick={() => navigate('/faq')}
          className="flex items-center gap-2 bg-[#F9A825] dark:bg-[#FFD54F] text-white dark:text-[#1C1B17] font-extrabold py-3 px-6 rounded-full shadow-2xl transition-all duration-300 hover:scale-110"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="hidden md:inline-block">FAQ</span>
        </button>
      </div>
    </>
  );
};

export default Hero;